import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, Radio, ShieldAlert } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { listSubjects, type Subject } from './api'
import { CollectedAt, Pill, label } from './components/primitives'

/**
 * The register: every file this platform holds.
 *
 * ## Two columns earn their place on the list rather than the detail
 *
 * **Collection** and **Public figure** are the two facts that change what the platform may
 * lawfully do with a file, and a screen that hid them until you opened a record is a screen
 * where nobody ever notices collection was left running on somebody. They are on the row.
 *
 * ## A merged file is not in this list
 *
 * The server excludes `MERGED` subjects unless a status filter asks for them: a tombstone
 * pointing at the surviving file belongs in a lookup by id, not in a list that reads as
 * "the people we hold files on".
 */
export function SubjectsPage() {
  const navigate = useNavigate()
  const [subjects, setSubjects] = useState<Subject[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [typeCode, setTypeCode] = useState('')
  const [watchlist, setWatchlist] = useState(false)
  const [collecting, setCollecting] = useState(false)
  const [q, setQ] = useState('')

  const load = useCallback(async () => {
    try {
      setError(null)
      setSubjects(await listSubjects({
        typeCode: typeCode || undefined,
        watchlist: watchlist ? 'true' : undefined,
        collectionEnabled: collecting ? 'true' : undefined,
        q: q || undefined,
      }))
    } catch (err) {
      setError(explain(err))
    }
  }, [typeCode, watchlist, collecting, q])

  useEffect(() => { void load() }, [load])

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Subjects"
        description="A subject is not necessarily a person — a topic, an organisation or a
                     place can hold a file too"
      />

      <Panel>
        <div className="flex flex-wrap items-end gap-2 p-2">
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Search</span>
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Name contains…"
                   className="h-7 rounded border border-input bg-background px-2 text-[12px]" />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Type</span>
            <select value={typeCode} onChange={(e) => setTypeCode(e.target.value)}
                    className="h-7 rounded border border-input bg-background px-2 text-[12px]">
              <option value="">All types</option>
              {['CANDIDATE', 'PARTY', 'COALITION', 'ORGANISATION', 'TOPIC', 'EXTERNAL_PERSON',
                'GEOGRAPHY_UNIT', 'MOBILIZER', 'VOTER', 'USER'].map((t) => (
                <option key={t} value={t}>{label(t)}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1 text-[12px]">
            <input type="checkbox" checked={watchlist}
                   onChange={(e) => setWatchlist(e.target.checked)} />
            Watchlist only
          </label>
          <label className="flex items-center gap-1 text-[12px]">
            <input type="checkbox" checked={collecting}
                   onChange={(e) => setCollecting(e.target.checked)} />
            Collection running
          </label>
        </div>
      </Panel>

      {error && <ErrorStrip message={error} />}
      {!subjects && !error && <LoadingBlock label="Loading subjects" />}

      {subjects && (
        <Panel>
          <ErpTable head={<>
            <Th>Subject</Th>
            <Th>Type</Th>
            <Th className="axis-hide-sm">Sensitivity</Th>
            <Th className="text-right">Items</Th>
            <Th>Last activity</Th>
            <Th>Collection</Th>
            <Th className="axis-hide-sm">Public figure</Th>
            <Th className="axis-hide-sm">Watch</Th>
          </>}>
              {subjects.length === 0 && (
                <EmptyRow colSpan={8}
                          message="No files match. Opening one is the New target screen." />
              )}
              {subjects.map((s) => (
                <tr key={s.id}
                    onClick={() => navigate(`/dossier/${s.id}`)}
                    className="cursor-pointer hover:bg-muted/40">
                  <Td>
                    <span className="font-medium">{s.displayName}</span>
                    {s.slug && (
                      <span className="ml-1 font-mono text-[10px] text-muted-foreground">
                        {s.slug}
                      </span>
                    )}
                  </Td>
                  <Td><Pill className="border-border">{label(s.subjectTypeCode)}</Pill></Td>
                  <Td className="axis-hide-sm">
                    {s.sensitivityCode === 'SENSITIVE' ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-amber-600">
                        <ShieldAlert className="size-3" />{label(s.sensitivityCode)}
                      </span>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">
                        {label(s.sensitivityCode)}
                      </span>
                    )}
                  </Td>
                  <Td className="text-right tabular-nums">{s.itemCount}</Td>
                  <Td><CollectedAt at={s.lastActivityAt} /></Td>
                  <Td>
                    {s.collectionEnabled ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-rose-600"
                            title={s.collectionPurpose ?? 'Collection is running'}>
                        <Radio className="size-3" />On
                      </span>
                    ) : (
                      <span className="text-[11px] text-muted-foreground">Off</span>
                    )}
                  </Td>
                  <Td className="axis-hide-sm">
                    {s.publicFigure
                      ? <Pill className="border-sky-500/30 bg-sky-500/10 text-sky-700
                                         dark:text-sky-400">
                          {label(s.publicFigureBasisCode)}
                        </Pill>
                      : <span className="text-[11px] text-muted-foreground">Private</span>}
                  </Td>
                  <Td className="axis-hide-sm">
                    {s.watchlist && <Eye className="size-3.5 text-primary" aria-label="Watched" />}
                  </Td>
                </tr>
              ))}
          </ErpTable>
        </Panel>
      )}
    </div>
  )
}
